"use client";

import { Button } from "@universe/ui/components/button";
import { cn } from "@universe/ui/lib/utils";
import { Check, Copy } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

interface CopyButtonProps {
  value: string;
  size?: "xs" | "sm" | "default";
  variant?: "ghost" | "outline";
  className?: string;
  iconClassName?: string;
}

/**
 * A button that copies a value to the clipboard and shows visual feedback.
 * Shows a Copy icon by default, and a green Check icon after copying.
 * Auto-resets after 2 seconds.
 */
export default function CopyButton({
  value,
  size = "default",
  variant = "ghost",
  className,
  iconClassName,
}: CopyButtonProps) {
  const tCommon = useTranslations("common");
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sizeClasses = {
    xs: "h-6 w-6",
    sm: "h-8 w-8",
    default: "",
  };

  const iconSizeClasses = {
    xs: "h-3 w-3",
    sm: "h-4 w-4",
    default: "h-4 w-4",
  };

  return (
    <Button
      variant={variant}
      size="icon"
      className={cn(sizeClasses[size], className)}
      aria-label={tCommon("copy")}
      onClick={handleCopy}
    >
      {copied ? (
        <Check
          className={cn(iconSizeClasses[size], "text-green-600", iconClassName)}
        />
      ) : (
        <Copy className={cn(iconSizeClasses[size], iconClassName)} />
      )}
    </Button>
  );
}
